import { useState, useMemo, useCallback, useRef } from "react"
import { useQuery } from "@tanstack/react-query"
import axios from "axios"
import usePlayers from "@/hooks/usePlayers"
import useUser from "@/hooks/useUser"
import { NestedPlayer } from "@/types/serializers"
import { UserIcon } from "@heroicons/react/20/solid"
import CreatableRecordSelector from "./CreatableRecordSelector"

const PlayerSelector = ({
  maxHeightClassName = "max-h-[235px]",
  label = "Player",
  playerId,
  selectedPlayerFallback,
  onChange,
  onBlur,
  onMaterializingChange,
  showMeChip,
  onMeChipUsed,
  showIsThisYou,
  onIsThisYouClick,
}: {
  maxHeightClassName?: string
  label?: string
  playerId: string | undefined
  selectedPlayerFallback?: NestedPlayer | null
  onChange: any
  onBlur: any
  onMaterializingChange?: (isMaterializing: boolean) => void
  showMeChip?: boolean
  onMeChipUsed?: () => void
  showIsThisYou?: boolean
  onIsThisYouClick?: (playerId: string) => void
}) => {
  const { players, createPlayerAsync } = usePlayers()
  const { user } = useUser()
  const inputId = `${label.toLowerCase().replace(/\s+/g, "-")}-player-selector`
  const [searchQuery, setSearchQuery] = useState("")
  const [isMaterializing, setIsMaterializing] = useState(false)

  // Cache discoverable players that have been selected, so they persist
  // in the list after the search query resets
  const selectedDiscoverableRef = useRef<Map<string, NestedPlayer>>(new Map())

  // Fetch discoverable ChessDrop members when user types a search query
  const { data: discoverableResults } = useQuery({
    queryKey: ["player-search", searchQuery],
    queryFn: async (): Promise<NestedPlayer[]> => {
      const res = await axios.get("/api/players", { params: { q: searchQuery } })
      return Array.isArray(res.data) ? res.data : []
    },
    enabled: searchQuery.trim().length >= 2,
    staleTime: 1000 * 30,
  })

  // Wrap onChange to handle discoverable player materialization
  const handleChange = useCallback(async (id: string | null) => {
    if (!id) {
      onChange(id)
      return
    }

    // Check if this is a discoverable result that needs materialization
    const allResults = [...players, ...(discoverableResults ?? [])]
    const selected = allResults.find((p) => p.id === id)

    if (selected?.is_chessdrop_user && selected?.discoverable_player_id) {
      // Create local linked player
      // Use the hook's createPlayerAsync instead of raw axios — centralizes
      // error handling, query invalidation, and event tracking
      setIsMaterializing(true)
      onMaterializingChange?.(true)
      try {
        const localPlayer = await createPlayerAsync({
          name: selected.name,
          discoverable_player_id: selected.discoverable_player_id,
        })
        onChange(localPlayer.id)
      } catch {
        // Do NOT fall back to foreign ID — clear selection.
        // The hook's onError already shows a toast, so no need to add another.
        onChange(null)
      } finally {
        setIsMaterializing(false)
        onMaterializingChange?.(false)
      }
    } else {
      if (id && discoverableResults) {
        const discoverable = discoverableResults.find((p) => p.id === id)
        if (discoverable?.is_chessdrop_user) {
          selectedDiscoverableRef.current.set(id, discoverable)
        }
      }
      onChange(id)
    }
  }, [onChange, players, discoverableResults, onMaterializingChange, createPlayerAsync])

  // Merge own players with discoverable results + cached selections
  const mergedPlayers = useMemo(() => {
    const ownIds = new Set(players.map((p) => p.id))
    const extras: NestedPlayer[] = []

    // Add cached discoverable selections (so they persist after query clears)
    for (const [id, player] of selectedDiscoverableRef.current) {
      if (!ownIds.has(id)) {
        extras.push(player)
        ownIds.add(id)
      }
    }

    // Add current search results
    if (discoverableResults?.length) {
      for (const p of discoverableResults) {
        if (!ownIds.has(p.id)) {
          extras.push(p)
          ownIds.add(p.id)
        }
      }
    }

    return extras.length ? [...players, ...extras] : players
  }, [players, discoverableResults])

  const myPlayerId = user?.player_id
  const myPlayer = myPlayerId
    ? players.find((p) => p.id === myPlayerId)
    : null

  const handleMeChipClick = () => {
    if (myPlayer) {
      onChange(myPlayer.id)
      onBlur()
      onMeChipUsed?.()
    }
  }

  const labelContent = (
    <div className="flex items-center gap-x-2">
      <span>{label}</span>
      {showMeChip && myPlayer && !playerId ? (
        <button
          type="button"
          onClick={handleMeChipClick}
          className="text-chessdrop-600 hover:text-chessdrop-700 inline-flex items-center gap-0.5 text-xs font-medium hover:underline"
        >
          <UserIcon className="size-3" />
          My Player
        </button>
      ) : null}
      {showIsThisYou && playerId && !myPlayerId ? (
        <button
          type="button"
          onClick={() => onIsThisYouClick?.(playerId)}
          className="text-xs font-medium text-gray-400 hover:text-gray-600 hover:underline"
        >
          Is this you?
        </button>
      ) : null}
    </div>
  )

  return (
    <div className="relative col-span-3">
      <CreatableRecordSelector
        createActionLabel="Create player"
        emptyActionLabel="a player"
        inputId={inputId}
        label={label}
        labelContent={labelContent}
        maxHeightClassName={maxHeightClassName}
        recordId={playerId}
        records={mergedPlayers}
        selectedRecordFallback={selectedPlayerFallback}
        onChange={handleChange}
        onBlur={onBlur}
        onCreateAsync={createPlayerAsync}
        onQueryChange={setSearchQuery}
        pinnedRecordId={myPlayerId}
        pinnedRecordLabel="My Player"
      />
      {isMaterializing && (
        <div className="absolute inset-0 flex items-center justify-center rounded-lg bg-white/60">
          <div className="h-4 w-4 animate-spin rounded-full border-2 border-gray-300 border-t-gray-600" />
        </div>
      )}
    </div>
  )
}

export default PlayerSelector
